import React from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { TalentCardData } from '../../types';
import { TalentPageCard, TalentPageCardContent, TalentPageCardHeader } from '../ui/TalentPageCard';
import RecommendedByAI from '../ui/RecommendedByAI';
import HighlightedText from '../ui/HighlightedText';
import { Badge } from '../ui/Badge';

interface PerformanceDashboardProps {
  talentCard: TalentCardData;
}

const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({ talentCard }) => {
  // Qualitative performance assessment system
  const getQualitativePerformance = (score: number, confidence: number): { level: number; assessment: string; color: string } => {
    const confidenceWeight = confidence || 0.5;
    
    // Map score to qualitative assessment levels
    let assessment: string;
    let level: number;
    
    if (score >= 4.5) {
      assessment = 'Exceptional';
      level = 5;
    } else if (score >= 3.5) {
      assessment = 'Exceeding';
      level = 4;
    } else if (score >= 2.5) {
      assessment = 'Meeting';
      level = 3;
    } else if (score >= 1.5) {
      assessment = 'Developing';
      level = 2;
    } else {
      assessment = 'Minimal';
      level = 1;
    }
    
    // Adjust based on confidence level
    if (confidenceWeight < 0.4 && level > 1) {
      level = Math.max(1, level - 1);
    }
    
    // Update assessment based on adjusted level
    const assessments = {
      5: 'Exceptional',
      4: 'Exceeding', 
      3: 'Meeting',
      2: 'Developing',
      1: 'Minimal'
    };
    
    assessment = assessments[level as keyof typeof assessments];
    
    // Gradient color mapping for qualitative levels
    const colors = {
      5: 'bg-emerald-600',   // Exceptional - Deep Green
      4: 'bg-emerald-400',   // Exceeding - Medium Green  
      3: 'bg-teal-500',      // Meeting - Positive Teal
      2: 'bg-orange-400',    // Developing - Orange
      1: 'bg-red-500'        // Minimal - Red
    };
    
    return {
      level,
      assessment,
      color: colors[level as keyof typeof colors] || 'bg-gray-300'
    };
  };

  const getPerformanceLevelColor = (level: number): string => {
    // Custom color scheme - lighter colors for lower scores
    const colors = {
      5: '#0d1321',   // Exceptional - Darkest
      4: '#1d2d44',   // Exceeding - Dark
      3: '#3e5c76',   // Meeting - Medium
      2: '#748cab',   // Developing - Light
      1: '#f0ebd8'    // Minimal - Lightest
    };
    return colors[level as keyof typeof colors] || '#9ca3af';
  };

  const getTextColor = (level: number): string => {
    // Use dark text for light backgrounds (levels 1-2), white text for dark backgrounds (levels 3-5)
    return level <= 2 ? '#1f2937' : '#ffffff';
  };

  const formatDimensionName = (dimension: string) => {
    return dimension
      .replace(/_/g, ' ')  // Replace underscores with spaces
      .replace(/([A-Z])/g, ' $1')  // Add space before capital letters
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  // Justification is now read directly from the database

  const scoringData = talentCard.scoringResults ? Object.entries(talentCard.scoringResults) : [];

  return (
    <TalentPageCard className="overflow-hidden">
      {/* Header */}
      <TalentPageCardHeader className="bg-gray-100 px-4 sm:px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-1">
                <h2 className="text-[var(--font-size-lg)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] text-gray-900">Dimension Analysis</h2>
                <RecommendedByAI size="sm" />
              </div>
              <p className="text-[var(--font-size-sm)] font-[var(--font-family-primary)] text-gray-600">Talent assessment across key dimensions</p>
            </div>
          </div>
          <div className="text-left sm:text-right">
            <div className="text-[var(--font-size-sm)] font-[var(--font-family-primary)] text-gray-500">Overall Score</div>
            <div className="text-[var(--font-size-2xl)] font-[var(--font-weight-bold)] font-[var(--font-family-primary)] text-gray-900">
              {scoringData.length > 0 
                ? (scoringData.reduce((sum, [, data]) => sum + data.score, 0) / scoringData.length).toFixed(1)
                : 'N/A'
              }/5
            </div>
          </div>
        </div>
      </TalentPageCardHeader>

      <TalentPageCardContent>
      {/* Dimension Insights */}
      {talentCard.calibrationInsights?.dimension_insights && (
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Strongest Dimensions */}
            {talentCard.calibrationInsights.dimension_insights.strongest_dimensions && 
             talentCard.calibrationInsights.dimension_insights.strongest_dimensions.length > 0 && (
              <div className="bg-green-50 rounded-lg p-4">
                <h5 className="text-[var(--font-size-xs)] font-[var(--font-weight-normal)] text-[#232F3E] mb-2 uppercase tracking-wide">
                  Strongest Dimensions
                </h5>
                <div className="flex flex-wrap gap-1">
                  {talentCard.calibrationInsights.dimension_insights.strongest_dimensions.map((dimension, index) => (
                    <Badge key={index} variant="success" className="text-[var(--font-size-xs)] text-white">
                      {dimension.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Development Dimensions */}
            {talentCard.calibrationInsights.dimension_insights.development_dimensions && 
             talentCard.calibrationInsights.dimension_insights.development_dimensions.length > 0 && (
              <div className="bg-amber-50 rounded-lg p-4">
                <h5 className="text-[var(--font-size-xs)] font-[var(--font-weight-normal)] text-[#232F3E] mb-2 uppercase tracking-wide">
                  Development Dimensions
                </h5>
                <div className="flex flex-wrap gap-1">
                  {talentCard.calibrationInsights.dimension_insights.development_dimensions.map((dimension, index) => (
                    <Badge key={index} variant="warning" className="text-[var(--font-size-xs)]">
                      {dimension.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Dimension Analysis Text */}
          {talentCard.calibrationInsights.dimension_insights.dimension_analysis && (
            <div className="bg-blue-50 border-l-4 border-[#0073BB] p-4 rounded-r-lg">
              <p className="text-[var(--font-size-sm)] text-[#232F3E] leading-relaxed">
                {talentCard.calibrationInsights.dimension_insights.dimension_analysis}
              </p>
            </div>
          )}
        </div>
      )}
      {/* Heatmap Legend - Positioned above table */}
      <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
        <div className="flex justify-center">
          <div className="flex flex-col items-center space-y-2">
            <div className="flex space-x-0.5 sm:space-x-1">
              <span className="text-[0.65rem] font-[var(--font-family-primary)] text-gray-600 font-[var(--font-weight-medium)] w-12 sm:w-16 text-center leading-tight">Minimal</span>
              <span className="text-[0.65rem] font-[var(--font-family-primary)] text-gray-600 font-[var(--font-weight-medium)] w-12 sm:w-16 text-center leading-tight">Developing</span>
              <span className="text-[0.65rem] font-[var(--font-family-primary)] text-gray-600 font-[var(--font-weight-medium)] w-12 sm:w-16 text-center leading-tight">Meeting</span>
              <span className="text-[0.65rem] font-[var(--font-family-primary)] text-gray-600 font-[var(--font-weight-medium)] w-12 sm:w-16 text-center leading-tight">Exceeding</span>
              <span className="text-[0.65rem] font-[var(--font-family-primary)] text-gray-600 font-[var(--font-weight-medium)] w-12 sm:w-16 text-center leading-tight">Exceptional</span>
            </div>
            <div className="flex space-x-0.5 sm:space-x-1">
              <div className="w-12 sm:w-16 h-2 sm:h-3 rounded-l" style={{backgroundColor: '#f0ebd8'}} />
              <div className="w-12 sm:w-16 h-2 sm:h-3" style={{backgroundColor: '#748cab'}} />
              <div className="w-12 sm:w-16 h-2 sm:h-3" style={{backgroundColor: '#3e5c76'}} />
              <div className="w-12 sm:w-16 h-2 sm:h-3" style={{backgroundColor: '#1d2d44'}} />
              <div className="w-12 sm:w-16 h-2 sm:h-3 rounded-r" style={{backgroundColor: '#0d1321'}} />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Card Layout (hidden on md+) */}
      <div className="md:hidden">
        <div className="space-y-4 p-4">
          {scoringData.map(([dimension, data], index) => {
            const performance = getQualitativePerformance(data.score, data.confidence || 0.5);
            
            return (
              <div key={dimension} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                {/* Dimension Name */}
                <div className="mb-3">
                  <h3 className="text-[var(--font-size-base)] font-[var(--font-weight-medium)] font-[var(--font-family-primary)] text-gray-900">
                    {formatDimensionName(dimension)}
                  </h3>
                </div>
                
                {/* Score Visualization */}
                <div className="mb-3">
                  <div className="text-[var(--font-size-xs)] font-[var(--font-weight-medium)] font-[var(--font-family-primary)] text-gray-500 uppercase tracking-wider mb-2">Performance Level</div>
                  <div 
                    className="w-full h-10 rounded-lg flex items-center justify-center font-[var(--font-weight-bold)] font-[var(--font-family-primary)] text-[var(--font-size-sm)] shadow-sm"
                    style={{
                      backgroundColor: getPerformanceLevelColor(performance.level),
                      color: getTextColor(performance.level)
                    }}
                  >
                    {performance.assessment}
                  </div>
                </div>
                
                {/* Confidence */}
                {data.confidence !== undefined && (
                  <div className="mb-3">
                    <div className="text-[var(--font-size-xs)] font-[var(--font-weight-medium)] font-[var(--font-family-primary)] text-gray-500 uppercase tracking-wider mb-2">Confidence</div>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gray-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.round(data.confidence * 100)}%` }}
                        />
                      </div>
                      <span className="text-[var(--font-size-sm)] font-[var(--font-family-primary)] text-gray-600 min-w-[3rem]">
                        {Math.round(data.confidence * 100)}%
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Justification Preview */}
                <div>
                  <div className="text-[var(--font-size-xs)] font-[var(--font-weight-medium)] font-[var(--font-family-primary)] text-gray-500 uppercase tracking-wider mb-2">Justification</div>
                  <div className="text-[var(--font-size-sm)] font-[var(--font-family-primary)] text-gray-700 leading-relaxed">
                    {data.reasoning ? (
                      <HighlightedText 
                        justification={data.reasoning}
                        segments={data.segments}
                      />
                    ) : (
                      'No justification available'
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop Table Layout (hidden on mobile) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-[var(--font-size-xs)] font-[var(--font-weight-medium)] font-[var(--font-family-primary)] text-gray-500 uppercase tracking-wider min-w-[120px]">
                Dimension
              </th>
              <th className="px-4 py-3 text-left text-[var(--font-size-xs)] font-[var(--font-weight-medium)] font-[var(--font-family-primary)] text-gray-500 uppercase tracking-wider min-w-[150px]">
                Score
              </th>
              <th className="px-4 py-3 text-left text-[var(--font-size-xs)] font-[var(--font-weight-medium)] font-[var(--font-family-primary)] text-gray-500 uppercase tracking-wider min-w-[100px]">
                Confidence
              </th>
              <th className="px-4 py-3 text-left text-[var(--font-size-xs)] font-[var(--font-weight-medium)] font-[var(--font-family-primary)] text-gray-500 uppercase tracking-wider min-w-[300px]">
                Justification
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {scoringData.map(([dimension, data], index) => {
              const performance = getQualitativePerformance(data.score, data.confidence || 0.5);
              
              return (
                <tr key={dimension} className="hover:bg-gray-50 transition-colors duration-150">
                  {/* Dimension */}
                  <td className="px-6 py-4">
                    <div className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)] font-[var(--font-family-primary)] text-gray-900">
                      {formatDimensionName(dimension)}
                    </div>
                  </td>

                  {/* Heatmap Score Visualization */}
                  <td className="px-2 py-1">
                    <div 
                      className="mx-2 my-1 h-12 rounded-lg flex items-center justify-center font-[var(--font-weight-bold)] font-[var(--font-family-primary)] text-[var(--font-size-xs)] shadow-sm transition-all duration-300"
                      style={{
                        backgroundColor: getPerformanceLevelColor(performance.level),
                        color: getTextColor(performance.level)
                      }}
                    >
                      {performance.assessment}
                    </div>
                  </td>

                  {/* Confidence */}
                  <td className="px-4 py-4">
                    {data.confidence !== undefined && (
                      <div className="flex flex-col space-y-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                          className="bg-gray-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.round(data.confidence * 100)}%` }}
                        />
                        </div>
                        <span className="text-[var(--font-size-xs)] font-[var(--font-family-primary)] text-gray-600 text-center">
                          {Math.round(data.confidence * 100)}%
                        </span>
                      </div>
                    )}
                  </td>

                  {/* Justification */}
                  <td className="px-4 py-4">
                    <div className="text-[var(--font-size-xs)] font-[var(--font-family-primary)] text-gray-700 leading-relaxed break-words">
                      {data.reasoning ? (
                        <HighlightedText
                          justification={data.reasoning}
                          segments={data.segments}
                        />
                      ) : (
                        'No justification available'
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty state when no scoring data */}
      {scoringData.length === 0 && (
        <div className="px-6 py-12 text-center">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-[var(--font-size-sm)] font-[var(--font-weight-medium)] font-[var(--font-family-primary)] text-gray-900">
            No performance data available
          </h3>
          <p className="mt-1 text-[var(--font-size-sm)] font-[var(--font-family-primary)] text-gray-500">
            Performance metrics will appear here once scoring is complete.
          </p>
        </div>
      )}
      </TalentPageCardContent>
    </TalentPageCard>
  );
};

export default PerformanceDashboard;